// Globales Gast-Panel: MCP-Connect (Migration 0080, seit dem MCP-Ausbau auch
// für projekt-skopierte Gäste) + E-Mail-Benachrichtigungs-Toggle. Weder task-
// noch projektbezogen — /api/me/tokens und /api/me/preferences sind für JEDEN
// externen Gast mit aktiver Einladung erreichbar, unabhängig vom Scope.
// Genutzt von GuestTaskView.tsx (inline) und GuestSettings.tsx (eigene Seite).

import { useEffect, useState } from "preact/hooks";
import { ChevronDown, Copy, KeyRound } from "lucide-preact";
import { api } from "../lib/api";
import { toastError, toastSuccess } from "../lib/toast";

import { Button } from "./ui/Button";

interface GuestTokenResponse {
  token: string;
  scopes: string;
}

interface MePreferences {
  channel_email: boolean;
}

/** Gleiche zwei Varianten wie ApiTokensSection.tsx (Settings, normale User):
 *  scope "local" (Default von `claude mcp add`) bindet nur im aktuellen
 *  Projektverzeichnis ein, "user" in allen Projekten — beide landen in
 *  `~/.claude.json`, NICHT in einer versionierten Datei. Absichtlich KEIN
 *  "project"-Scope: der schreibt in `.mcp.json` im Repo-Root, das i. d. R.
 *  eingecheckt wird — damit würde das persönliche Bearer-Token in die
 *  Git-Historie und zu jedem Mitentwickler gelangen. */
function mcpAddCommand(scope: "local" | "user", token: string): string {
  return `claude mcp add --transport http juuplan ${window.location.origin}/api/mcp --header "Authorization: Bearer ${token}" --scope ${scope}`;
}

interface Props {
  /** GuestSettings.tsx IST die Einstellungsseite — dort von Anfang an offen. */
  defaultOpen?: boolean;
  /** Eingeklappter Header nur nötig, wenn er neben anderem Inhalt sitzt (GuestTaskView). */
  collapsible?: boolean;
}

export function GuestConnectPanel(props: Props): preact.JSX.Element {
  const { defaultOpen = false, collapsible = true } = props;
  const [open, setOpen] = useState(defaultOpen);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState<GuestTokenResponse | null>(null);
  const [emailOn, setEmailOn] = useState<boolean | null>(null);
  const [savingEmail, setSavingEmail] = useState(false);
  // Getrennt von `emailOn`, weil ein GET-Fehlschlag sonst wie eine echte
  // Präferenz aussieht (`api()` toastet NUR Schreibfehler, siehe lib/api.ts —
  // ein fehlgeschlagenes GET ist app-weit stumm). Ohne diesen eigenen
  // Zustand würde der Schalter nach einem Ladefehler einfach "an" und
  // bedienbar erscheinen, als sei das die echte Einstellung.
  const [emailLoadFailed, setEmailLoadFailed] = useState(false);

  useEffect(() => {
    if (!open || emailOn !== null) return;
    void api<MePreferences>("/api/me/preferences")
      .then((p) => setEmailOn(p.channel_email))
      .catch(() => {
        setEmailLoadFailed(true);
        toastError("E-Mail-Einstellung konnte nicht geladen werden.");
        // Trotzdem einen Wert setzen, sonst bleibt der Schalter für immer
        // deaktiviert — "an" ist die serverseitig sicherere Vermutung
        // (lieber eine überflüssige Mail als eine verpasste).
        setEmailOn(true);
      });
  }, [open, emailOn]);

  const connect = async (): Promise<void> => {
    setConnecting(true);
    try {
      const result = await api<GuestTokenResponse>("/api/me/tokens", {
        method: "POST",
      });
      setConnected(result);
    } catch {
      // api.ts toasted
    } finally {
      setConnecting(false);
    }
  };

  const copyCommand = async (command: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(command);
      toastSuccess("Befehl kopiert.");
    } catch {
      toastError("Kopieren fehlgeschlagen — bitte manuell markieren.");
    }
  };

  const commandBlock = (command: string): preact.JSX.Element => (
    <div class="relative">
      <code class="block whitespace-pre-wrap break-all rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1.5 pr-9 text-micro text-[color:var(--color-on-surface)]">
        {command}
      </code>
      <button
        type="button"
        aria-label="Befehl kopieren"
        title="Kopieren"
        onClick={() => void copyCommand(command)}
        class="absolute top-1 right-1 rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container)]"
      >
        <Copy size={14} aria-hidden="true" />
      </button>
    </div>
  );

  const toggleEmail = async (next: boolean): Promise<void> => {
    setSavingEmail(true);
    setEmailOn(next);
    try {
      await api<MePreferences>("/api/me/preferences", {
        method: "PATCH",
        body: { channel_email: next },
      });
    } catch {
      // api.ts toastet bereits; hier nur den optimistischen Schalter zurückdrehen.
      setEmailOn(!next);
    } finally {
      setSavingEmail(false);
    }
  };

  const body = (
    <div class="border-t border-[color:var(--color-outline-variant)] px-4 py-3 space-y-4">
      <section>
        <h2 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-1.5">
          Mit Claude Code verbinden
        </h2>
        {connected ? (
          <div class="space-y-2.5">
            <p class="text-xs text-[color:var(--color-on-surface-variant)]">
              Befehl kopieren und im Terminal ausführen. Das Token wird{" "}
              <strong>nie wieder angezeigt</strong>.
            </p>
            <div>
              <p class="mb-1 text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Nur für dieses Projekt
              </p>
              {commandBlock(mcpAddCommand("local", connected.token))}
            </div>
            <div>
              <p class="mb-1 text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Für alle Projekte (user-weit)
              </p>
              {commandBlock(mcpAddCommand("user", connected.token))}
            </div>
          </div>
        ) : (
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={() => void connect()}
            disabled={connecting}
          >
            {connecting ? "Verbinde…" : "Verbinden"}
          </Button>
        )}
      </section>

      <section>
        <h2 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-1.5">
          E-Mail-Benachrichtigungen
        </h2>
        <label class="flex items-center gap-2 text-sm text-[color:var(--color-on-surface)]">
          <input
            type="checkbox"
            checked={emailOn ?? true}
            disabled={emailOn === null || savingEmail}
            onChange={(e) => void toggleEmail((e.target as HTMLInputElement).checked)}
          />
          Bei neuen Kommentaren oder Statusänderungen per E-Mail benachrichtigen
        </label>
        {emailLoadFailed && (
          <p class="mt-1 text-label text-[color:var(--color-error)]">
            Konnte den gespeicherten Wert nicht laden — Schalter zeigt einen Standardwert.
          </p>
        )}
      </section>
    </div>
  );

  if (!collapsible) {
    return (
      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
        {body}
      </div>
    );
  }

  return (
    <div class="mb-4 rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        class="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm font-medium text-[color:var(--color-on-surface)]"
      >
        <span class="flex items-center gap-2">
          <KeyRound size={16} aria-hidden="true" />
          Verbinden &amp; Benachrichtigungen
        </span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          class={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && body}
    </div>
  );
}
